/** Wire-shape normalizer for `GET /api/user-management/v1/features`. */

import type { Feature } from '@/userManagement/domain/entities/Feature';

interface CategoryFeatureWire {
  featureId?: unknown;
  featureName?: unknown;
  isSensitive?: unknown;
  supportsRead?: unknown;
  supportsWrite?: unknown;
  supportsDelete?: unknown;
  supportsCentralAccess?: unknown;
  supportsExcelExport?: unknown;
  isActive?: unknown;
  isRemovedFromErp?: unknown;
}

function readIsSensitive(value: unknown): boolean {
  return value === true;
}

/** Missing Supports* from older APIs defaults to true (all checkboxes shown). */
function readSupports(value: unknown): boolean {
  return value !== false;
}

interface CategoryWire {
  categoryId?: unknown;
  categoryName?: unknown;
  features?: unknown;
}

interface CategorizedFeaturesWire {
  categories?: unknown;
  totalFeatures?: unknown;
}

function isCategorizedShape(raw: unknown): raw is CategorizedFeaturesWire {
  return typeof raw === 'object' && raw !== null && 'categories' in raw && Array.isArray((raw as { categories: unknown }).categories);
}

function isLegacyFeatureArray(raw: unknown): raw is Feature[] {
  if (!Array.isArray(raw)) return false;
  if (raw.length === 0) return true;
  const sample = raw[0] as Record<string, unknown> | null;
  return !!sample && typeof sample === 'object' && 'id' in sample && 'name' in sample;
}

function flattenCategorizedFeatures(categories: CategoryWire[]): Feature[] {
  const out: Feature[] = [];
  const seenFeatureIds = new Set<number>();
  for (const cat of categories) {
    if (!cat || typeof cat !== 'object') continue;
    const categoryName = typeof cat.categoryName === 'string' ? cat.categoryName : '';
    const features = Array.isArray(cat.features) ? (cat.features as CategoryFeatureWire[]) : [];
    for (const f of features) {
      if (!f || typeof f !== 'object') continue;
      const featureId = typeof f.featureId === 'number' ? f.featureId : Number(f.featureId);
      if (!Number.isFinite(featureId) || seenFeatureIds.has(featureId)) continue;
      seenFeatureIds.add(featureId);
      const featureName = typeof f.featureName === 'string' ? f.featureName : '';
      out.push({
        id: featureId,
        name: featureName,
        category: categoryName,
        parentId: null,
        children: [],
        isSensitive: readIsSensitive(f.isSensitive),
        supportsRead: readSupports(f.supportsRead),
        supportsWrite: readSupports(f.supportsWrite),
        supportsDelete: readSupports(f.supportsDelete),
        supportsCentralAccess: readSupports(f.supportsCentralAccess),
        supportsExcelExport: readSupports(f.supportsExcelExport),
        isActive: f.isActive !== false,
        isRemovedFromErp: f.isRemovedFromErp === true
      });
    }
  }
  return out;
}

/**
 * Accepts the live `{ categories: [...], totalFeatures }` shape, the legacy flat `Feature[]` shape,
 * or unknown/empty payloads. Always returns a flat `Feature[]` so application/domain code stays unchanged.
 */
export function normalizeFeaturesResponse(raw: unknown): Feature[] {
  if (isCategorizedShape(raw)) {
    return flattenCategorizedFeatures(raw.categories as CategoryWire[]);
  }
  if (isLegacyFeatureArray(raw)) {
    return raw.map((feature) => ({
      ...feature,
      isSensitive: feature.isSensitive ?? false,
      supportsRead: feature.supportsRead ?? true,
      supportsWrite: feature.supportsWrite ?? true,
      supportsDelete: feature.supportsDelete ?? true,
      supportsCentralAccess: feature.supportsCentralAccess ?? true,
      supportsExcelExport: feature.supportsExcelExport ?? true,
      isActive: feature.isActive !== false,
      isRemovedFromErp: feature.isRemovedFromErp === true
    }));
  }
  console.warn('[userManagement] normalizeFeaturesResponse: unrecognized features payload shape; using []', {
    received: raw === undefined ? 'undefined' : raw === null ? 'null' : typeof raw
  });
  return [];
}
